import { useEffect, useRef, useState } from 'react'
import interact from 'interactjs'

function onStartDrag(e, options){
  const { interaction } = e
  const {
    callbacks: { dragStart },
    createElementClone,
    disabled,
    documentElementId,
    draggableZIndex,
    draggingClassName,
  } = options

  e.preventDefault()
  
  if (!disabled && interaction.pointerIsDown && !interaction.interacting()){
    // Callback if provided
    if (dragStart) dragStart()
    
    const original = e.currentTarget
    const originalRect = interact.getElementRect(original)

    // Dragged Element
    const draggedElement = createElementClone ? original.cloneNode(true) : original

    // Add Dragging Class
    if (draggingClassName) draggedElement.classList.add(draggingClassName)

    // Set Clone Properties
    if (createElementClone && draggedElement.getAttribute('data-interaction-started') !== 'true'){
      // Position the clone
      draggedElement.style.position = 'absolute'
      draggedElement.style.top = `${originalRect.top}px`
      draggedElement.style.left = `${originalRect.left}px`
      draggedElement.style.width = `${originalRect.width}px`
      draggedElement.style.height = `${originalRect.height}px`
      draggedElement.style.zIndex = draggableZIndex || originalRect.zIndex

      // Insert the clone to the page
      document.getElementById(documentElementId).appendChild(draggedElement)

      // Set the param to avoid loop
      draggedElement.setAttribute('data-interaction-started', 'true')

      // Start a drag interaction targeting the clone
      interaction.start({ name: 'drag' }, e.interactable, draggedElement)
    }
  }
}

function onDragMove(e, options, coordsRef, setCoords){
  const { disabled, translateElement = true, zoom } = options

  const { dx, dy } = e

  if (!disabled){
    setCoords((currentCoords) => {
      const updatedCoords = {
        ...currentCoords,
        x: currentCoords.x + dx / zoom,
        y: currentCoords.y + dy / zoom,
      }

      coordsRef.current = updatedCoords
      return updatedCoords
    })

    // Store the dragged position in the data-x/data-y attribute
    if (translateElement){
      const { target } = e
  
      const x = (parseFloat(target.getAttribute('data-x')) || 0) + e.dx
      const y = (parseFloat(target.getAttribute('data-y')) || 0) + e.dy
  
      // Translate the element
      target.style.webkitTransform = `translate(${x}px, ${y}px)`
      target.style.transform = `translate(${x}px, ${y}px)`
  
      // Update the position attributes
      target.setAttribute('data-x', x)
      target.setAttribute('data-y', y)
    }
  }
}

function onEndMove(e, options, coordsRef){
  const {
    callbacks: { moveEnd },
    createElementClone,
    documentElementId,
  } = options

  if (moveEnd){
    moveEnd(coordsRef.current, e)
  }

  // Remove the clone from document
  if (createElementClone && documentElementId){
    const draggedElement = e.target
    draggedElement.parentNode.removeChild(draggedElement)
  }
}

function onResizing(e, options, coordsRef, setCoords){
  const {
    deltaRect: { left, top },
    rect: { width, height },
  } = e

  const {
    anchorSize, disabled, selected, zoom = 1,
  } = options || {}

  if (selected && !disabled){
    setCoords((currentCoords) => {
      const updatedCoords = {
        x: currentCoords.x + left / zoom,
        y: currentCoords.y + top / zoom,
        width: (width - anchorSize * 2) / zoom,
        height: (height - anchorSize * 2) / zoom,
      }

      coordsRef.current = updatedCoords
      return updatedCoords
    })
  }
}

const getResizableOptions = (options, coordsRef, setCoords) => {
  const { resizable, resizableMargin } = options || {}

  if (!resizable){
    return false
  }

  return {
    onmove: e => onResizing(e, options, coordsRef, setCoords),
    onend: e => onEndMove(e, options, coordsRef),
    edges: {
      left: true,
      right: true,
      top: true,
      bottom: true,
    },
    margin: resizableMargin,
  }
}

const defaultState = {
  height: 0,
  width: 0,
  x: 0,
  y: 0,
}

function useDraggable(dragElement, options = {}){
  const {
    disabled, position, resizable, restriction, selected, zoom,
  } = options

  const {
    height, width, x, y,
  } = position || {}

  const [coords, setCoords] = useState(defaultState)

  // Store in ref so that we can get latest coords
  // when move ends without calling setState which
  // causes double request/render
  const coordsRef = useRef(coords)

  useEffect(() => {
    setCoords(prevState => ({
      height: height || prevState.height,
      width: width || prevState.width,
      x: x || x === 0 ? x : prevState.x,
      y: y || y === 0 ? y : prevState.y,
    }))
  }, [x, y])

  useEffect(() => {
    if (!dragElement || !dragElement.current){
      return null
    }

    const interactable = interact(dragElement.current)

    // Resizable
    const resizableOptions = getResizableOptions(options, coordsRef, setCoords)

    // Interactable
    interactable
      .resizable(resizableOptions)
      .draggable({
        onstart: e => onStartDrag(e, options),
        onmove: e => onDragMove(e, options, coordsRef, setCoords),
        onend: e => onEndMove(e, options, coordsRef),
        restrict: {
          restriction: restriction || null,
        },
      })
    
    return () => (interactable ? interactable.unset() : null)
  }, [disabled, resizable, selected, zoom])

  return coords
}

export default useDraggable
